import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { useModule } from '../hooks/useModules';
import { usePermissions } from '../hooks/usePermissions';
import { Button } from '../components/ui/Button';
import { AccessGate } from '../components/ui/AccessGate';
import { ShieldAlert } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireModule?: string;
  requireAction?: string | string[];
  requireAllActions?: boolean;
  requireTier?: readonly string[];
  requireSystemAdmin?: boolean;
  requireNode?: boolean;
}

// --- Main component ---

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireModule, 
  requireAction,
  requireAllActions = false,
  requireTier,
  requireSystemAdmin = false,
  requireNode = false
}) => {
  const { isSignedIn, isLoading, isMaintenanceMode, retrySync, skipSync } = useCurrentUser();
  const { isGlobal, node, brand } = useTenant();
  const { can: canHook, role, isAdmin, isSystemAdmin: isSystemAdminRole, isSuperAdmin: isSuperAdminRole } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  
  const isPublicPath = React.useMemo(() => {
    const publicPaths = ['/sign-in', '/sign-up', '/global'];
    return location.pathname === '/' || publicPaths.some(p => location.pathname.startsWith(p));
  }, [location.pathname]);

  // Dev-only render diagnostics. The counter/log run in an effect (not during render)
  // so the ref is only touched outside of the render phase.
  const renderCount = React.useRef(0);

  React.useEffect(() => {
    renderCount.current++;
    if (import.meta.env.DEV) console.log(`[ProtectedRoute RENDER #${renderCount.current}] path=${location.pathname}, isLoading=${isLoading}, isSignedIn=${isSignedIn}`);
    if (import.meta.env.DEV) console.log(`[ProtectedRoute EFFECT #${renderCount.current}] isLoading=${isLoading}, isSignedIn=${isSignedIn}, isMaintenanceMode=${isMaintenanceMode}, path=${location.pathname}, isPublicPath=${isPublicPath}`);
  }, [isLoading, isSignedIn, isMaintenanceMode, location.pathname, isPublicPath]);

  // Call hooks unconditionally at the top level
  const moduleEnabled = useModule(requireModule || '');
  const hasModule = requireModule ? (moduleEnabled || isSystemAdminRole || isSuperAdminRole) : true;

  const hasPermission = (!requireAction || isSystemAdminRole || isSuperAdminRole)
    ? true
    : Array.isArray(requireAction)
      ? requireAllActions
        ? requireAction.every(a => canHook(a as any))
        : requireAction.some(a => canHook(a as any))
      : canHook(requireAction as any);

  // Hooks must be called unconditionally before any early returns (Rules of Hooks)
  const [loadElapsed, setLoadElapsed] = React.useState(0);

  // Reset the loading clock when loading finishes — render-phase adjustment
  // (the ticker itself runs in an interval callback that the effect schedules).
  const [prevRouteLoading, setPrevRouteLoading] = React.useState(isLoading);
  if (prevRouteLoading && !isLoading) {
    setPrevRouteLoading(false);
    setLoadElapsed(0);
  } else if (!prevRouteLoading && isLoading) {
    setPrevRouteLoading(true);
  }

  React.useEffect(() => {
    if (!isLoading) return;
    const interval = setInterval(() => {
      setLoadElapsed(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isLoading]);

  // Maintenance Override
  // We only show the full-screen block if sync has fundamentally failed OR if the backend
  // is down AND the user is NOT already signed in (as their session init would likely fail)
  if (isMaintenanceMode && !isSignedIn) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-10 text-center border border-gray-100 dark:bg-slate-900">
          <div className="mb-8 relative inline-block">
            <div className="w-20 h-20 bg-amber-50 text-amber-500 rounded-2xl flex items-center justify-center animate-pulse">
              <ShieldAlert size={40} />
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full border-4 border-white" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-3 underline decoration-amber-500/30 decoration-4 underline-offset-4 dark:text-white">Connectivity Issue</h2>
          <p className="text-gray-600 mb-8 leading-relaxed text-sm">
            Our servers are currently unreachable. We've temporarily limited access to prevent data loss or sync errors.
            <span className="block mt-2 font-medium text-gray-900 dark:text-white">Please try again in a few moments.</span>
          </p>
          
          <div className="flex flex-col gap-3">
            <Button 
              variant="primary" 
              className="w-full h-12 text-base shadow-lg shadow-brand-primary/20"
              onClick={() => {
                // Clear maintenance mode and try again
                retrySync(false);
              }}
            >
              Check Connection
            </Button>
            <Button 
              variant="ghost" 
              className="w-full text-gray-400 hover:text-gray-600"
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
          </div>
          
          <div className="mt-10 pt-8 border-t border-gray-50">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400">
              {brand?.name} Operational Status: Degraded
            </p>
          </div>
        </div>
      </div>
    );
  }

  // loadElapsed hooks are now above the maintenance early-return (Rules of Hooks compliance)

  if (isLoading && !isPublicPath) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-[60vh] p-6 text-center animate-in fade-in duration-300">
        <div className="w-12 h-12 border-4 border-black dark:border-white border-t-[#0044FF] rounded-full animate-spin mb-6" />
        
        <h3 className="font-display font-black text-xl uppercase tracking-tight text-black dark:text-white">
          {isMaintenanceMode ? 'Establishing Connection' : 'Loading Workspace'}
        </h3>
        
        <p className="mt-2 text-xs font-mono uppercase tracking-widest text-gray-500 max-w-sm">
          {loadElapsed >= 2
            ? 'Backend sync is taking longer than usual...'
            : 'Synchronizing permissions and organization hierarchy...'}
        </p>

        {loadElapsed >= 3 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <Button
              variant="primary"
              size="sm"
              onClick={() => skipSync()}
              className="bg-black text-white dark:bg-white dark:text-black font-mono font-black text-xs uppercase tracking-widest px-4 py-2 rounded-none border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000]"
            >
              Skip Sync & Enter
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => retrySync(false)}
              className="bg-white text-black dark:bg-slate-900 dark:text-white font-mono font-black text-xs uppercase tracking-widest px-4 py-2 rounded-none border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000]"
            >
              Retry Connection
            </Button>
          </div>
        )}
      </div>
    );
  }

  // (a) Auth check - ONLY perform if NOT on a public path
  if (!isSignedIn && !isPublicPath) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  const hasActiveNode = Boolean(node && !isGlobal && node.id !== 'platform-root');
  const hasRequiredTier = !requireTier || isSystemAdminRole || isSuperAdminRole || (node?.tier && requireTier.includes(node.tier));
  const goBack = () => navigate(-1);

  // (a.2) Active node check
  if (requireNode && !hasActiveNode) {
    return (
      <AccessGate
        reason="org-required"
        canResolve
        onResolve={() => navigate('/dashboard')}
        onBack={goBack}
      />
    );
  }

  // (a.5) System Admin role check
  if (requireSystemAdmin && !isSystemAdminRole && !isSuperAdminRole) {
    return <AccessGate reason="platform-admin" role={role} onBack={goBack} />;
  }

  // (b) Module check — show module disabled state.
  // Checked before the tier gate: "this isn't turned on for your org" is a more
  // accurate message than "upgrade your plan" when the module is simply disabled.
  if (requireModule && !hasModule) {
    return (
      <AccessGate
        reason="module"
        subject={requireModule}
        canResolve={isAdmin}
        onResolve={isAdmin ? () => navigate('/settings/modules') : undefined}
        onBack={goBack}
      />
    );
  }

  // (c) Tier check — show upgrade prompt
  if (!hasRequiredTier) {
    return (
      <AccessGate
        reason="tier"
        currentTier={node?.tier || 'LOCAL'}
        requiredTiers={requireTier!}
        canResolve={isAdmin}
        onResolve={isAdmin ? () => navigate('/settings/billing') : undefined}
        onBack={goBack}
      />
    );
  }

  // (d) Role/capability check — show role denied state
  if (requireAction && !hasPermission) {
    return <AccessGate reason="permission" role={role} onBack={goBack} />;
  }

  return <>{children}</>;
};
